import { useCallback } from "react";

import type { Collision, Data, DragEndEvent as DndKitDragEndEvent, UniqueIdentifier } from "@skilitics/drag-drop";

import {
  CONDITION_TYPE,
  EFFECT_TYPE,
  GROUP_CONDITION_NAME,
  NEGATION_CONDITION_NAME,
  TYPE_BUILDABLE,
  TYPE_CANCEL,
  TYPE_DROPPABLE,
  TYPE_INSTANCE,
  TYPE_ROOT_DROPPABLE,
} from "../constants";
import type { DragEndEventResult } from "./dndEventTypes";
import { draggableDataSchema, droppableDataSchema } from "./dndSchemas";

type Sortable = { group?: UniqueIdentifier; index?: number };

export type DragEndEvent = {
  active: {
    id: UniqueIdentifier;
    data: Data;
    sortable?: Sortable;
  };
  over: {
    id: UniqueIdentifier;
    disabled: boolean;
    data: Data;
    sortable?: Sortable;
  } | null;
  collisions: Collision[];
};

type DragManager = { collisionObserver: { collisions: Collision[] } };

export const useOnDragEnd = () => {
  return useCallback((eventParams: DndKitDragEndEvent, manager?: DragManager) => {
    const { source, target } = eventParams.operation;
    const collisions = manager?.collisionObserver.collisions ?? [];
    if (!source) return;
    return onDragEnd({
      active: {
        id: source.id,
        data: source.data,
        sortable: (source as { sortable?: Sortable }).sortable,
      },
      over: target
        ? {
            id: target.id,
            disabled: target.disabled,
            data: target.data,
            sortable: (target as { sortable?: Sortable }).sortable,
          }
        : null,
      collisions,
    });
  }, []);
};

export const onDragEnd = ({ active, over, collisions }: DragEndEvent): DragEndEventResult => {
  if (!over || !collisions?.length || over.disabled || active.id === over.id) return;

  const { rule: activeRule } = draggableDataSchema.parse(active.data);
  const { rule: overRule, disabled } = droppableDataSchema.parse(over.data);

  if (disabled || overRule.type === TYPE_CANCEL || activeRule.ruleType !== overRule.ruleType) return;

  const overIndex = over.sortable?.index;
  const overGroupId = over.sortable?.group != null ? Number(over.sortable.group) : undefined;
  const overCanHaveChildren =
    overRule.type === TYPE_DROPPABLE &&
    (overRule.typeId === GROUP_CONDITION_NAME || overRule.typeId === NEGATION_CONDITION_NAME);

  // --- Buildables ---

  if (activeRule.type === TYPE_BUILDABLE && activeRule.ruleType === CONDITION_TYPE) {
    const parentId =
      overRule.type === TYPE_ROOT_DROPPABLE ? undefined : overCanHaveChildren ? overRule.id : overGroupId;
    return {
      eventType: "addCondition",
      nodeId: activeRule.nodeId,
      typeId: activeRule.typeId,
      parentId,
      index: overIndex,
    };
  }

  if (activeRule.type === TYPE_BUILDABLE && activeRule.ruleType === EFFECT_TYPE) {
    return {
      eventType: "addEffect",
      nodeId: activeRule.nodeId,
      typeId: activeRule.typeId,
      index: overIndex,
    };
  }

  // --- Existing conditions ---

  if (activeRule.type === TYPE_INSTANCE && activeRule.ruleType === CONDITION_TYPE) {
    const activeGroupId = active.sortable?.group != null ? Number(active.sortable.group) : undefined;

    // Dropped onto a group/negation Droppable zone
    if (overCanHaveChildren) {
      // Already inside this group — no-op
      if (activeGroupId === overRule.id) return;

      return {
        eventType: "moveCondition",
        id: activeRule.id,
        parentId: overRule.id,
        index: overIndex,
      };
    }

    // Moving to a different group
    if (activeGroupId !== overGroupId && overGroupId !== undefined) {
      return {
        eventType: "moveCondition",
        id: activeRule.id,
        parentId: overGroupId,
        index: overIndex,
      };
    }

    // Reordering within the same group
    if (overIndex !== undefined) {
      return {
        eventType: "reorderCondition",
        id: activeRule.id,
        index: overIndex,
      };
    }
  }

  // --- Existing effects ---

  if (activeRule.type === TYPE_INSTANCE && activeRule.ruleType === EFFECT_TYPE && overIndex !== undefined) {
    return {
      eventType: "reorderEffect",
      id: activeRule.id,
      index: overIndex,
    };
  }
};
